import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { MarketDate } from '@/features/bidding/utils/date/marketDate'

/**
 * Typed wrapper around `useSearchParams` for the new bid detail route's
 * query string params. Distinct from the URL path params (`:marketProgram`,
 * `:bidType`) which are read via {@link useBidDetailRouteParams}.
 *
 * Query params owned by this view:
 *
 * - `portfolioCode` — string, e.g. `"FI-A"`
 * - `deliveryDay` — ISO date string (`YYYY-MM-DD`)
 * - `activationGroupUuid` — optional UUID
 */

export type BidDetailQueryParams = Readonly<{
  portfolioCode: string | null
  deliveryDay: MarketDate | null
  activationGroupUuid: string | null
}>

export type BidDetailQueryParamsUpdate = Partial<{
  portfolioCode: string | null
  deliveryDay: MarketDate | null
  activationGroupUuid: string | null
}>

export function useBidDetailQueryParams(): readonly [
  BidDetailQueryParams,
  (update: BidDetailQueryParamsUpdate) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams()

  const portfolioCode = searchParams.get('portfolioCode')
  const deliveryDayIso = searchParams.get('deliveryDay')
  const activationGroupUuid = searchParams.get('activationGroupUuid')

  const params = useMemo<BidDetailQueryParams>(
    () => ({
      portfolioCode,
      deliveryDay: deliveryDayIso ? new MarketDate(deliveryDayIso) : null,
      activationGroupUuid,
    }),
    [portfolioCode, deliveryDayIso, activationGroupUuid],
  )

  const setParams = useCallback(
    (update: BidDetailQueryParamsUpdate) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          applyUpdate(next, 'portfolioCode', update.portfolioCode)
          applyUpdate(next, 'deliveryDay', update.deliveryDay ? update.deliveryDay.toISODate() : update.deliveryDay)
          applyUpdate(next, 'activationGroupUuid', update.activationGroupUuid)

          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  return [params, setParams]
}

function applyUpdate(params: URLSearchParams, key: string, value: string | null | undefined) {
  if (value === undefined) {
    // Caller did not mention this key — leave it alone.
    return
  }
  if (value === null) {
    params.delete(key)

    return
  }
  params.set(key, value)
}
